import React from 'react'
import AgTechTourHelpButton from '@agtech/web/components/App/Tour/AgTechTourHelpButton';
import { MyPlanningTasksDialogTourConfig } from './MyPlanningTasksDialogTourConfig';
import dayjs from 'dayjs';
import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton, AgTechActionButton } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechVerticalPopUpMenu, AgTechVerticalPopUpMenuSection } from '@agtech/web/components/Menus/AgTechVerticalPopUpMenu';
import AgTechViewDialog from '@agtech/web/components/Portals/Dialogs/AgTechViewDialog';
import AgTechGrid from '@agtech/web/components/Grids/AgTechGrid';
import { gridItems } from '@agtech/web/components/Grids/Data/AgTechGridProps';
import { useAgTechQueryCombination } from '@agtech/core/data/queries/AgTechDataQueries';
import { Index } from '@agtech/core/utilities/AgTechUtilities';
import { SalePlanningTask } from '@sales/data/planningTasks/PlanningTaskData';
import { PlanningTaskDataGetMyTasksQuery } from '@sales/data/planningTasks/PlanningTaskQueries';
import { usePlanningTaskCompleteAction } from '@sales/data/planningTasks/PlanningTaskDataActions';
import { SaleDataGetSalesQuery } from '@sales/data/sales/SaleDataQueries';
import { useSaleDataChangeSelectedSaleAction } from '@sales/data/sales/SaleDataActions';

type MyPlanningTask = SalePlanningTask & { saleName: string }

const MyPlanningTasksDialog = () => {
    const dialogQuery = useAgTechQueryCombination({
        firstQuery: { query: PlanningTaskDataGetMyTasksQuery },
        secondQuery: { query: SaleDataGetSalesQuery },
        projection: (tasks, sales): MyPlanningTask[] => {
            const salesById = Index(sales, s => s.saleId);

            return Object.values(tasks).map(task => ({
                ...task,
                saleName: salesById[task.saleId]?.saleName ?? 'Unknown Sale'
            }));
        }
    });

    return (
        <AgTechViewDialog
            data={{ query: dialogQuery }}
            structure={{
                header: () => ({
                    header: 'My Tasks',
                    detail: 'Planning tasks assigned to you, across every sale.',
                    settings: {
                        content: {
                            body: () => <AgTechTourHelpButton tourConfig={MyPlanningTasksDialogTourConfig} />
                        }
                    }
                }),
                width: '900px'
            }}
        >
            {(tasks, refresh, closeDialog) => (
                <div id='my-tasks-grid' className='stretched row-based container'>
                    <AgTechGrid
                        data={{ itemQuery: gridItems(tasks) }}
                        header={{ title: 'My Tasks' }}
                        columns={[
                            {
                                header: 'Task',
                                value: item => item.taskName,
                                classes: 'bg-slate-100 emphasized'
                            },
                            {
                                header: 'Sale',
                                value: item => item.saleName
                            },
                            {
                                header: 'Priority',
                                value: item => item.priority.toString()
                            },
                            {
                                header: 'End Date',
                                value: item => dayjs(item.endDate).format('MM/DD/YYYY')
                            },
                            {
                                header: 'Status',
                                value: item => item.isComplete ? 'Complete' : 'Incomplete'
                            }
                        ]}
                        isRowEntityTheSame={(prev, curr) => {
                            return prev.taskName === curr.taskName &&
                                prev.isComplete === curr.isComplete &&
                                prev.saleName === curr.saleName &&
                                prev.nextNotificationDate === curr.nextNotificationDate;
                        }}
                        searchConfig={{
                            options: [
                                { field: 'Task', value: item => item.taskName },
                                { field: 'Sale', value: item => item.saleName }
                            ],
                            placeholder: 'Search your tasks...'
                        }}
                        noRecordsConfig={{
                            message: 'You have no open planning tasks assigned to you.'
                        }}
                        actions={{
                            rendering: item => (
                                <AgTechActionButton classes='stretched centered container'>
                                    <MyPlanningTaskActionMenu task={item} closeDialog={closeDialog} />
                                </AgTechActionButton>
                            )
                        }}
                    />
                </div>
            )}
        </AgTechViewDialog>
    )
}

const MyPlanningTaskActionMenu = (props: { task: MyPlanningTask, closeDialog: (e: React.MouseEvent<HTMLElement>) => void }) => {
    const completeTaskAction = usePlanningTaskCompleteAction();
    const saleChangeAction = useSaleDataChangeSelectedSaleAction();

    return (
        <AgTechVerticalPopUpMenu>
            <AgTechVerticalPopUpMenuSection header="Task">
                <AgTechButton
                    text={props.task.isComplete ? "Reopen Task" : "Complete Task"}
                    icon={props.task.isComplete ? "fas fa-rotate-left" : "fas fa-check"}
                    action={e => e.actions.executeDataActionWithVisual(
                        completeTaskAction,
                        {
                            submittedEntity: { task: props.task, isComplete: !props.task.isComplete }
                        },
                        {
                            message: props.task.isComplete ? 'Reopening task...' : 'Completing task...',
                            delay: 300
                        }
                    )}
                />
                <AgTechButton
                    text="Go To Sale"
                    icon="fas fa-arrow-right"
                    action={async (e: AgTechClickEventHandler) => {
                        await e.actions.executeDataActionWithVisual(
                            saleChangeAction,
                            { submittedEntity: props.task.saleId },
                            { message: 'Switching to sale...', delay: 300 }
                        );

                        props.closeDialog(e.clickEvent);
                    }}
                />
            </AgTechVerticalPopUpMenuSection>
        </AgTechVerticalPopUpMenu>
    )
}

MyPlanningTasksDialog.ButtonProps = (props?: AgTechButtonProps) => ({
    text: 'My Tasks',
    icon: 'fas fa-list-check',
    ...props,
    action: (e: AgTechClickEventHandler) => e.dialogs.openDialog({
        dialog: () => <MyPlanningTasksDialog />
    })
})

MyPlanningTasksDialog.Button = (props: AgTechButtonProps) => {
    return <AgTechButton {...MyPlanningTasksDialog.ButtonProps(props)} />
}

export default MyPlanningTasksDialog
